/**
 * Production-grade logging service
 * Replaces all console statements with proper logging levels and formatting
 * In production, sends logs to Cloud Logging for automatic archiving
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

interface LogEntry {
  message: string;
  level: LogLevel;
  timestamp: string;
  data?: any;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  context?: any;
}

class Logger {
  /**
   * Safe environment variable accessor
   * Handles both browser (React) and Node.js environments
   */
  private static getEnvVar(key: string, defaultValue: string = ''): string {
    try {
      // CRA/webpack only inlines static process.env.X access — dynamic process.env[key] is often undefined in the browser.
      if (key === 'NODE_ENV') {
        if (typeof process !== 'undefined' && process.env && typeof process.env.NODE_ENV === 'string') {
          return process.env.NODE_ENV;
        }
        return defaultValue;
      }
      if (typeof process !== 'undefined' && process.env) {
        const value = process.env[key];
        return (value && typeof value === 'string') ? value : defaultValue;
      }
      return defaultValue;
    } catch (error) {
      // Fail silently - return default value
      return defaultValue;
    }
  }

  /**
   * Check if running in browser environment
   */
  private static isBrowser(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Check if running in production environment
   */
  private static isProduction(): boolean {
    return this.getEnvVar('NODE_ENV') === 'production';
  }

  // Defensive initialization: handle undefined NODE_ENV gracefully
  private static logLevel: LogLevel = (() => {
    try {
      const env = Logger.getEnvVar('NODE_ENV');
      // Default to DEBUG if NODE_ENV is undefined or not 'production'
      return env === 'production' ? LogLevel.WARN : LogLevel.DEBUG;
    } catch (error) {
      // Fallback to DEBUG if there's any error accessing process.env
      return LogLevel.DEBUG;
    }
  })();

  // Cloud Logging client (lazy-loaded, only in production/backend)
  private static cloudLoggingClient: any = null;
  private static isInitialized = false;

  /**
   * Initialize Cloud Logging (backend only, production only)
   */
  private static async initializeCloudLogging(): Promise<void> {
    // Skip in browser environment
    if (this.isInitialized || this.isBrowser()) {
      return; // Frontend or already initialized
    }

    try {
      // Only initialize in production backend
      const gcloudProject = this.getEnvVar('GCLOUD_PROJECT');
      if (this.isProduction() && gcloudProject) {
        // Resolve Node require dynamically so browser bundles do not try to include backend-only packages.
        const nodeRequire = Function('return typeof require !== "undefined" ? require : null')() as
          | ((id: string) => unknown)
          | null;
        if (!nodeRequire) {
          return;
        }
        const loggingModule = nodeRequire('@google-cloud/logging') as { Logging?: new (opts: { projectId: string }) => unknown };
        const Logging = loggingModule?.Logging;
        if (!Logging) {
          return;
        }
        this.cloudLoggingClient = new Logging({
          projectId: gcloudProject
        });
        this.isInitialized = true;
      }
    } catch (error) {
      // Fail silently - fallback to console logging
      console.warn('[Logger] Cloud Logging initialization failed, using console:', error);
    }
  }

  /**
   * Send log to Cloud Logging (backend production only)
   */
  private static async sendToCloudLogging(entry: LogEntry): Promise<void> {
    // Skip in browser environment
    if (!this.cloudLoggingClient || this.isBrowser()) {
      return; // Frontend or Cloud Logging not available
    }

    try {
      const log = this.cloudLoggingClient.log('app');
      const metadata = {
        resource: { type: 'global' },
        severity: entry.level.toUpperCase(),
        labels: {
          environment: this.getEnvVar('NODE_ENV', 'development'),
          service: 'tranzit-backend'
        }
      };

      const logData: any = {
        message: entry.message,
        timestamp: entry.timestamp,
        ...(entry.data && { data: entry.data }),
        ...(entry.error && { error: entry.error }),
        ...(entry.context && { context: entry.context })
      };

      await log.write(log.entry(metadata, logData));
    } catch (error) {
      // Fail silently - don't break the app if Cloud Logging fails
      console.error('[Logger] Failed to send to Cloud Logging:', error);
    }
  }

  /**
   * Debug level logging - only in development
   */
  static debug(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      const entry: LogEntry = {
        message,
        level: LogLevel.DEBUG,
        timestamp: this.getTimestamp(),
        data
      };

      try {
        console.log(`[${entry.timestamp}] [DEBUG] ${message}`, data || '');
      } catch (error) {
        console.log(`[DEBUG] ${message}`, data || '');
      }

      // Send to Cloud Logging in production backend (skip in browser)
      if (this.isProduction() && !this.isBrowser()) {
        this.initializeCloudLogging().then(() => this.sendToCloudLogging(entry)).catch(() => {
          // Silently fail - don't break logging if Cloud Logging fails
        });
      }
    }
  }

  /**
   * Info level logging - general information
   */
  static info(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.INFO)) {
      const entry: LogEntry = {
        message,
        level: LogLevel.INFO,
        timestamp: this.getTimestamp(),
        data
      };

      try {
        console.info(`[${entry.timestamp}] [INFO] ${message}`, data || '');
      } catch (error) {
        console.info(`[INFO] ${message}`, data || '');
      }

      // Send to Cloud Logging in production backend (skip in browser)
      if (this.isProduction() && !this.isBrowser()) {
        this.initializeCloudLogging().then(() => this.sendToCloudLogging(entry)).catch(() => {
          // Silently fail - don't break logging if Cloud Logging fails
        });
      }
    }
  }

  /**
   * Warning level logging - potential issues
   */
  static warn(message: string, data?: any): void {
    if (this.shouldLog(LogLevel.WARN)) {
      const entry: LogEntry = {
        message,
        level: LogLevel.WARN,
        timestamp: this.getTimestamp(),
        data
      };

      try {
        console.warn(`[${entry.timestamp}] [WARN] ${message}`, data || '');
      } catch (error) {
        console.warn(`[WARN] ${message}`, data || '');
      }

      // Send to Cloud Logging in production backend (skip in browser)
      if (this.isProduction() && !this.isBrowser()) {
        this.initializeCloudLogging().then(() => this.sendToCloudLogging(entry)).catch(() => {
          // Silently fail - don't break logging if Cloud Logging fails
        });
      }
    }
  }

  /**
   * Error level logging - errors that need attention
   * Formats error objects as strings to prevent console from treating them as actual errors
   */
  static error(message: string, error?: Error | null, context?: any): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      const entry: LogEntry = {
        message,
        level: LogLevel.ERROR,
        timestamp: this.getTimestamp(),
        error: error ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        } : undefined,
        context
      };

      try {
        // Format error as string to prevent console from showing it as an actual error
        const errorString = error 
          ? `Error: ${error.name} - ${error.message}${error.stack ? `\n${error.stack}` : ''}`
          : '';
        const contextString = context ? JSON.stringify(context, null, 2) : '';
        
        // Use console.error for actual error logging, but format it clearly as a log entry
        const logMessage = `[${entry.timestamp}] [ERROR] ${message}`;
        if (errorString) {
          console.error(logMessage, '\n', errorString, contextString ? `\nContext: ${contextString}` : '');
        } else {
          console.error(logMessage, contextString ? `\nContext: ${contextString}` : '');
        }
      } catch (err) {
        // Fallback to simple error logging if formatting fails
        console.error(`[ERROR] ${message}`, error?.message || '', context || '');
      }

      // Send to Cloud Logging in production backend (skip in browser)
      if (this.isProduction() && !this.isBrowser()) {
        this.initializeCloudLogging().then(() => this.sendToCloudLogging(entry)).catch(() => {
          // Silently fail - don't break logging if Cloud Logging fails
        });
      }
    }
  }

  /**
   * Check if we should log at this level
   */
  private static shouldLog(level: LogLevel): boolean {
    try {
      const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
      const currentLevelIndex = levels.indexOf(this.logLevel);
      const requestedLevelIndex = levels.indexOf(level);

      // Defensive check: if logLevel is invalid, default to showing all logs
      if (currentLevelIndex === -1) {
        return true;
      }

      return requestedLevelIndex >= currentLevelIndex;
    } catch (error) {
      // If shouldLog fails, allow the log to proceed (fail open)
      return true;
    }
  }

  /**
   * Get formatted timestamp
   */
  private static getTimestamp(): string {
    try {
      return new Date().toISOString();
    } catch (error) {
      // Fallback to simple timestamp if ISO string fails
      return new Date().toString();
    }
  }

  /**
   * Set log level (for testing)
   */
  static setLogLevel(level: LogLevel): void {
    this.logLevel = level;
  }

  /**
   * Get current log level
   */
  static getLogLevel(): LogLevel {
    return this.logLevel;
  }
}

export default Logger;
